import { useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Bell, MapPin, Tag, Building2, Users, Landmark, AlertCircle, Calendar, ExternalLink, CheckCircle } from "lucide-react";
import { AlertTheme, AlertSourceType } from "@/data/mockAlerts";
import { moroccoRegions } from "@/data/parties";
import { useAlerts } from "@/hooks/useAlerts";
import { useToast } from "@/hooks/use-toast";
import { getApiUrl } from "@/utils/apiConfig";

const themes: AlertTheme[] = ['Politique', 'Économie', 'Santé', 'Éducation', 'Environnement', 'Infrastructure'];

const getSourceIcon = (sourceType: AlertSourceType) => {
  switch (sourceType) {
    case "Institution": return <Building2 className="w-5 h-5 text-blue-500" />;
    case "Parlement": return <Landmark className="w-5 h-5 text-amber-500" />;
    case "Parti": return <Users className="w-5 h-5 text-green-500" />;
    case "Région": return <MapPin className="w-5 h-5 text-purple-500" />;
  }
};

const labels = {
  fr: {
    title: "Mes Alertes",
    subtitle: "Suivez l'actualité politique et institutionnelle de votre région en temps réel.",
    filterRegion: "Filtrer par Région",
    filterTheme: "Thématique d'intérêt",
    allRegions: "Toutes les régions",
    allThemes: "Toutes les thématiques",
    noAlerts: "Aucune alerte correspondante.",
    new: "Nouveau",
    important: "Important"
  },
  ar: {
    title: "تنبيهاتي",
    subtitle: "تابع الأخبار السياسية والمؤسساتية في جهتك في الوقت الفعلي.",
    filterRegion: "تصفية حسب الجهة",
    filterTheme: "الموضوع المفضل",
    allRegions: "جميع الجهات",
    allThemes: "جميع المواضيع",
    noAlerts: "لا توجد تنبيهات مطابقة.",
    new: "جديد",
    important: "هام"
  },
  zgh: {
    title: "ⵜⵉⵏⵖⵎⵉⵙⵉⵏ ⵉⵏⵓ",
    subtitle: "ⴹⴼⵕ ⵜⵉⵏⵖⵎⵉⵙⵉⵏ ⵜⵉⵙⵔⵜⴰⵏⵉⵏ ⴷ ⵜⵉⵎⵔⵙⴰⵍ ⵏ ⵜⵎⵏⴰⴹⵜ ⵏⵏⴽ ⴳ ⵓⴽⵓⴷ ⵉⵍⴰⵏ.",
    filterRegion: "ⵙⵜⵉ ⵙ ⵜⵎⵏⴰⴹⵜ",
    filterTheme: "ⴰⵙⵏⵜⵍ",
    allRegions: "ⵎⴰⵕⵕⴰ ⵜⵉⵎⵏⴰⴹⵉⵏ",
    allThemes: "ⵎⴰⵕⵕⴰ ⵉⵙⵏⵜⴰⵍ",
    noAlerts: "ⵓⵔ ⵍⵍⵉⵏⵜ ⵜⵏⵖⵎⵉⵙⵉⵏ.",
    new: "ⴰⵎⴰⵢⵏⵓ",
    important: "ⴰⵅⴰⵜⴰⵔ"
  }
};

const CitizenAlertsPage = () => {
  const { lang } = useI18n();
  const l = labels[lang] || labels.fr;

  const [regionFilter, setRegionFilter] = useState<string>("Toutes");
  const [themeFilter, setThemeFilter] = useState<string>("Tous");
  const { alerts, readIds, markAsRead } = useAlerts();
  const { toast } = useToast();
  const [isSubscribing, setIsSubscribing] = useState(false);

  const subscribeToPush = async () => {
    setIsSubscribing(true);
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        throw new Error("Les notifications Push ne sont pas supportées par votre navigateur.");
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error("Vous avez refusé l'autorisation pour les notifications.");
      }

      const registration = await navigator.serviceWorker.register('/sw.js');
      
      // Clef publique VAPID (factice pour cette démo, doit être générée sur le serveur en production)
      const publicVapidKey = "BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U";
      
      const urlBase64ToUint8Array = (base64String: string) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicVapidKey)
      });

      // Envoi au backend
      const response = await fetch(`${getApiUrl()}/newsletter.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'subscribe_push',
          subscription: subscription
        })
      });

      if (response.ok) {
        toast({ title: "Succès", description: "Vous êtes abonné aux alertes en temps réel !" });
      } else {
        throw new Error("Erreur lors de l'enregistrement serveur.");
      }

    } catch (err: any) {
      toast({ title: "Erreur", description: err.message, variant: "destructive" });
    } finally {
      setIsSubscribing(false);
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (alert.isActive === false) return false;
    if (alert.status === 'brouillon') return false;
    if (alert.status === 'programme' && alert.scheduledFor) {
      if (new Date() < new Date(alert.scheduledFor)) return false;
    }
    const matchRegion = regionFilter === "Toutes" || alert.region === "Toutes" || alert.region === regionFilter;
    const matchTheme = themeFilter === "Tous" || alert.theme === themeFilter;
    return matchRegion && matchTheme;
  }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <div className="space-y-8 animate-in fade-in duration-500 max-w-4xl mx-auto pb-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground flex items-center gap-3">
            <Bell className="w-8 h-8 text-primary" />
            {l.title}
          </h1>
          <p className="text-muted-foreground font-body mt-2">
            {l.subtitle}
          </p>
        </div>
        <Button 
          variant="outline" 
          className="gap-2 shrink-0 border-primary/20 hover:bg-primary/5"
          onClick={subscribeToPush}
          disabled={isSubscribing}
        >
          <Bell className="w-4 h-4" /> 
          {isSubscribing ? "Activation..." : "M'abonner aux notifications"}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-card p-4 rounded-2xl shadow-sm border border-border">
        <div>
          <label className="text-xs font-semibold text-muted-foreground mb-1.5 block flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" /> {l.filterRegion}
          </label>
          <Select value={regionFilter} onValueChange={setRegionFilter}>
            <SelectTrigger className="w-full bg-background border-input font-body">
              <SelectValue placeholder={l.allRegions} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Toutes">{l.allRegions}</SelectItem>
              {moroccoRegions.map(r => (
                <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
           <label className="text-xs font-semibold text-muted-foreground mb-1.5 block flex items-center gap-1">
            <Tag className="w-3.5 h-3.5" /> {l.filterTheme}
          </label>
          <Select value={themeFilter} onValueChange={setThemeFilter}>
            <SelectTrigger className="w-full bg-background border-input font-body">
              <SelectValue placeholder={l.allThemes} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Tous">{l.allThemes}</SelectItem>
              {themes.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="relative border-l-2 border-primary/20 ml-4 md:ml-8 pl-6 md:pl-10 space-y-10 py-4">
        {filteredAlerts.length === 0 ? (
           <div className="text-center py-10">
              <AlertCircle className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground font-body">{l.noAlerts}</p>
           </div>
        ) : (
          filteredAlerts.map(alert => {
            const isFresh = new Date(alert.date) > new Date(new Date().setDate(new Date().getDate() - 7));
            const isRead = readIds.includes(alert.id);
            return (
              <div 
                key={alert.id} 
                className={`relative group ${isRead ? 'opacity-80' : ''}`}
                onMouseEnter={() => markAsRead(alert.id)}
              >
                <div className="absolute -left-[35px] md:-left-[51px] top-2 flex items-center justify-center w-10 h-10 rounded-full bg-card border-2 border-background shadow-sm group-hover:scale-110 transition-transform z-10">
                  {getSourceIcon(alert.sourceType)}
                </div>
                
                <div className="bg-card rounded-2xl p-0 shadow-sm border border-border overflow-hidden hover:shadow-md transition-shadow">
                  {alert.imageUrl && (
                    <div className="w-full h-40 bg-muted relative overflow-hidden">
                      <img src={alert.imageUrl} alt={alert.theme} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <Badge className="absolute bottom-3 left-3 bg-background/90 text-foreground border-none font-body text-xs shadow-sm">
                        {alert.theme}
                      </Badge>
                    </div>
                  )}

                  <div className={`p-5 ${alert.imageUrl ? 'pt-4' : ''}`}>
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {!alert.imageUrl && (
                        <Badge variant="secondary" className="font-body text-[10px] bg-muted text-muted-foreground">
                          {alert.theme}
                        </Badge>
                      )}
                      <span className="text-[11px] font-semibold text-primary/80 flex items-center gap-1 font-mono tracking-tight">
                        <Calendar className="w-3 h-3" /> {alert.date}
                      </span>
                      {alert.region !== "Toutes" && (
                        <Badge variant="outline" className="font-body text-[10px] ml-auto border-primary/30 text-primary">
                          {alert.region}
                        </Badge>
                      )}
                    </div>

                    <h3 className="text-lg font-display font-bold text-foreground leading-tight mb-2">
                      {alert.title[lang] || alert.title.fr}
                    </h3>
                    
                    <p className="text-sm font-body text-muted-foreground mb-4">
                      {alert.description[lang] || alert.description.fr}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-border/50">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-foreground">{alert.sourceName}</span>
                      </div>
                      <div className="flex gap-2">
                        {alert.isImportant && (
                           <Badge className="bg-destructive/10 text-destructive border-transparent hover:bg-destructive/20 text-[10px] uppercase font-bold tracking-wider">
                             {l.important}
                           </Badge>
                        )}
                        {isFresh && (
                          <Badge className="bg-primary text-primary-foreground border-transparent text-[10px] uppercase font-bold tracking-wider">
                             {l.new}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CitizenAlertsPage;
